<script setup lang="ts">
definePageMeta({
  pageTransition: false
})
import { computed, onMounted, onUnmounted, ref, type ComponentPublicInstance, type Ref, reactive, watch } from 'vue'
import { defineAsyncComponent } from 'vue'
const Waterfall = defineAsyncComponent(() => import('vue-waterfall-plugin-next').then((m) => m.Waterfall))
import 'vue-waterfall-plugin-next/dist/style.css'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { ElMessage } from 'element-plus'
import { renderMessage, WAIT_FOR_LOADING_TIME } from '@/utils/util'
import { useRouter } from 'vue-router'
import {
  type MemorialPostDTO,
  type ListMemorialPostResponse,
  TYPE_STORY,
  mediaPreviewList,
  mediaPreviewIndex,
  isMediaPreviewOpen,
  mediaCanEdit,
  type LifeFileDTO,
  type LifeFileWithRelationDTO,
  listStoryPostByPage,
  isInMediaFolder,
  isMediaDeletable,
  mediaPreviewType
} from '@/composables/memorize/memorize'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import loading from '@/assets/loading.png'
import error from '@/assets/error.png'
import BaseLayout from '@/components/business/layout/BaseLayout.vue'
import { titleBar } from '@/composables/menu/title'
import { useServicesStore } from '@/composables/memorize/store'
import { isSetRealName } from '@/composables/user/users'
import { isNotEmpty } from '@/utils/util'
import { useI18n } from 'vue-i18n'
import { auditModeShow } from '@/utils/util'

import { useScheduleStore } from '@/composables/schedule/schedule'

import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)

const scheduleStore = useScheduleStore()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const lastId: Ref<string | null> = ref(null)
const pageSize = ref(20)
const totalSize = ref(0)
const postList: Ref<Array<MemorialPostDTO>> = ref([])

const showFooter = ref(true)
const footerStatus = ref('none')
const hasMoreItems = ref(true)

const serviceId = ref('')
serviceId.value = route.params.id as string
const locatePostId = ref()
const MfooterVisible = ref(false)
const scheduleParams = scheduleStore.getActionDialogParams()
const mActionOnce = { addStory: isNotEmpty(route.query.addStory) }

const removeQueryParams = (name?: string) => {
  if (name) {
    const { [name]: _, ...rest } = route.query
    console.log('rest', rest)
    router.replace({ query: rest })
  } else {
    router.replace({ query: {} })
  }
}

// SSR Data Fetching
const { data: ssrData } = await useAsyncData(`story-posts-${route.params.id}`, async () => {
  serviceId.value = route.params.id as string
  const { data } = await listStoryPostByPage(serviceId.value, null, pageSize.value)
  // Ensure data is serializable and break any potential proxy chains
  return data.value ? JSON.parse(JSON.stringify(data.value)) : null
})

onMounted(() => {
  console.log('=== onMounted', serviceId.value)

  locatePostId.value = route.query.postId as string

  if (locatePostId.value) {
    pageSize.value = 50
  }

  window.addEventListener('scroll', scrollListener)

  if (!ssrData.value || ssrData.value.code !== CODE_OK) {
    getData()
  }

  if (mActionOnce.addStory && scheduleParams) {
    setTimeout(() => {
      router.push(
        `/addPhoto?type=${scheduleParams.type}&serviceId=` +
          serviceId.value +
          '&name=' +
          scheduleParams.name +
          '&tag=' +
          scheduleParams.tag +
          '&key=' +
          scheduleParams.key
      )
    }, 400)
  }

  if (import.meta.client && route.query.addStory) {
    removeQueryParams('addStory')
  }
})

const dataState = ref(ssrData.value && ssrData.value.code === CODE_OK ? 'done' : 'loading')

function getData() {
  MfooterVisible.value = false
  loadPost(true)
  setTimeout(() => {
    if (dataState.value === 'none') {
      dataState.value = 'loading'
    }
  }, WAIT_FOR_LOADING_TIME)
}

onUnmounted(() => {
  window.removeEventListener('scroll', scrollListener)
})

function tryAgain() {
  if (postList.value.length == 0) {
    loadPost(true)
  } else {
    loadPost(false, lastId.value)
  }
}

function onDelete(postId: string) {
  console.log('onDelete', postId)
  postList.value = postList.value.filter((post) => post.id != postId)
  if (postList.value.length === 0) {
    dataState.value = 'empty'
  }
}

async function loadPost(isInitial: boolean = false, last: string | null = null) {
  if (isInitial) {
    lastId.value = null
  }

  footerStatus.value = 'loading'

  const { data, error, statusCode } = await listStoryPostByPage(serviceId.value, last, pageSize.value)
  setTimeout(() => {
    MfooterVisible.value = true
  }, 500)
  if (error.value) {
    handleErr(data, error, statusCode)

    if (last === null) {
      dataState.value = 'error'
    } else {
      footerStatus.value = 'failed'
    }
  } else {
    const resData = data as unknown as Ref<CommonResponse<ListMemorialPostResponse>>
    if (resData.value.code === CODE_OK) {
      // success
      const response: ListMemorialPostResponse = resData.value.data

      if (last === null) {
        postList.value = response.items
        if (postList.value.length > 0) {
          dataState.value = 'done'
        } else {
          dataState.value = 'empty'
        }
        overflowNotMember.value = response.nonMemberStorageOverflow && response.items.length > 0
      } else {
        postList.value.push(...response.items)
      }
      totalSize.value = response.total
      footerStatus.value = 'none'
      hasMoreItems.value = true

      if (response.items.length === 0) {
        hasMoreItems.value = false
      } else {
        const newLastId = response.items[response.items.length - 1].id
        if (last !== null && newLastId === last) {
          hasMoreItems.value = false
        } else {
          lastId.value = newLastId
        }
      }

      if (locatePostId.value && !located.value) {
        toLocate()
      }
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      if (last === null) {
        dataState.value = 'error'
      } else {
        footerStatus.value = 'failed'
      }
    }
  }
}

const scrollListener = function () {
  // Get the current scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  // Get the maximum scroll height of the document
  const scrollHeight = document.documentElement.scrollHeight
  // Get the height of the viewport
  const clientHeight = document.documentElement.clientHeight
  // Check if the user has scrolled to the bottom
  if (scrollTop + clientHeight + 10 >= scrollHeight) {
    console.log('reached bottom', hasMoreItems.value, footerStatus.value)
    if (hasMoreItems.value) {
      loadPost(false, lastId.value)
    }
  }
}

function onPostMediaClicked(post: MemorialPostDTO, item: LifeFileDTO) {
  const lifeFiles: Array<LifeFileWithRelationDTO> = post.files.map(
    (item) =>
      ({
        ...item,
        creatorRelation: post.creatorRelation,
        updaterRelation: post.updaterRelation
      }) as LifeFileWithRelationDTO
  )

  mediaPreviewList.value = lifeFiles
  mediaPreviewIndex.value = lifeFiles.findIndex((i) => i.id === item.id)
  mediaPreviewType.value = ''
  isMediaPreviewOpen.value = true
  mediaCanEdit.value = canEdit.value
  isInMediaFolder.value = false
  isMediaDeletable.value = true
  isMediaDeletable.value = false
}

const itemRefs: Map<string, HTMLElement> = new Map()
const playAnim = ref(false)
const located = ref(false)

function toLocate() {
  console.log('toLocate')
  setTimeout(() => {
    if (locatePostId.value && !located.value) {
      findAndScroll(locatePostId.value, 100)
    }
  }, 500)
}

// Function to set references to the DOM elements
const setItemRef = (el: Element | ComponentPublicInstance | null, id: string): void => {
  // Ensure the ref is an HTMLElement
  if (el && '$el' in el) {
    el = (el as ComponentPublicInstance).$el as HTMLElement
  }
  if (el instanceof HTMLElement) {
    itemRefs.set(id, el)
  } else {
    itemRefs.delete(id)
  }
}

const findAndScroll = (key: string, offset: number = 0) => {
  const element = itemRefs.get(key)
  if (element) {
    // Get the top position of the element relative to the viewport
    const elementTop = element.getBoundingClientRect().top
    // Calculate the absolute position to scroll to, considering the offset
    const scrollPosition = window.scrollY + elementTop - offset

    // Scroll to the calculated position
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
    located.value = true
    setTimeout(() => {
      playAnim.value = true
    }, 600)
  } else {
    console.log('Element not found:', itemRefs)
  }
}

const medias = computed(() => postList.value.map((item) => new ViewCardItem(item)))

const options = reactive({
  // 唯一key值
  rowKey: 'id',
  // 卡片之间的间隙
  gutter: 10,
  // 是否有周围的gutter
  hasAroundGutter: false,
  // 卡片在PC上的宽度
  width: 380,
  // 自定义行显示个数，主要用于对移动端的适配
  breakpoints: {
    // 1200: {
    //     // 当屏幕宽度小于等于1200
    //     rowPerView: 3,
    // },
    1000: {
      // 当屏幕宽度小于等于800
      rowPerView: 2
    },
    500: {
      // 当屏幕宽度小于等于500
      rowPerView: 1
    }
  },
  // 动画效果
  animationEffect: 'animate__fadeInUp',
  // 动画时间
  animationDuration: 500,
  // 动画延迟
  animationDelay: 200,
  // 背景色
  backgroundColor: 'transparent',
  // imgSelector
  imgSelector: 'src',
  // crossOrigin
  crossOrigin: true,
  // 加载配置
  loadProps: {
    loading,
    error,
    ratioCalculator: (width: number, height: number) => {
      console.log('w:', width, 'h:', height)
      // 我设置了最小宽高比
      const minRatio = 1 / 2
      const maxRatio = 2 / 1
      // 获取当前图片的比例
      const curRatio = width / height
      // 如果当前图片比列不在此范围内，我们取最小或者最大值
      if (curRatio < minRatio) {
        return minRatio
      } else if (curRatio > maxRatio) {
        return maxRatio
      } else {
        return curRatio
      }
    }
  },
  // 是否懒加载
  lazyload: true,
  align: 'center'
})

class ViewCardItem {
  post: MemorialPostDTO
  src: string
  name: string

  constructor(_post: MemorialPostDTO) {
    this.post = _post
    if (_post && _post.files && _post.files.length > 0) {
      this.src = _post?.files[0]?.file?.middleQuality || _post?.files[0]?.file?.url || ''
    } else {
      this.src = ''
    }
    this.name = _post.content
  }
}

const dataLoading = ref(true)

function afterRender() {
  dataLoading.value = false
  console.log('计算完成')
}

const serviceDetailsStore = useServicesStore()
const serviceDetails = computed(() => serviceDetailsStore.searchForItem(serviceId.value))

const canEdit = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'm_story')
  if (serviceDetails.value?.privilege == null) {
    return false
  } else {
    return permission?.value && permission.value !== 'READ'
  }
})
async function handleCreate() {
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()
    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  console.log('serviceDetails!!!!!', serviceDetails)
  router.push(
    `/addPhoto?type=${TYPE_STORY}&serviceId=` + serviceId.value + '&name=' + serviceDetails.value.life.name.fullName
  )
}

const overflowNotMember = ref(false)

function handleSsrData() {
  if (!ssrData.value) return
  const response = ssrData.value.data as ListMemorialPostResponse
  if (ssrData.value.code === CODE_OK && response) {
    postList.value = response.items
    if (postList.value.length > 0) {
      dataState.value = 'done'
    } else {
      dataState.value = 'empty'
    }
    overflowNotMember.value = response.nonMemberStorageOverflow && response.items.length > 0
    totalSize.value = response.total
    hasMoreItems.value = true
    if (response.items.length === 0) {
      hasMoreItems.value = false
    } else {
      lastId.value = postList.value[postList.value.length - 1].id
    }
    MfooterVisible.value = true

    if (locatePostId.value && !located.value) {
      toLocate()
    }
  }
}

if (ssrData.value && ssrData.value.code === CODE_OK) {
  handleSsrData()
}

const pageTitle = computed(() => {
  const fullName = serviceDetails.value?.life?.name?.fullName
  if (auditModeShow.value) {
    return t('m.tag_title_audit', { fullName: fullName })
  } else if (inMiniprograme.value) {
    return t('m.tag_title_mini', { fullName: fullName })
  }
  return fullName ? t('m.memorial_hall_title', { name: fullName }) : t('m.memorial_hall_default')
})

const requestURL = useRequestURL()
const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: () => t('m.story_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogDescription: () => t('m.story_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogImage: () => serviceDetails.value?.life?.avatar?.url || serviceDetails.value?.life?.avatar?.middleQuality,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})
</script>
<template>
  <BaseLayout py-1 :state="dataState" @retry="getData">
    <ClientOnly>
      <div v-if="medias.length > 0 && canEdit" w-full flex="~ row" justify-between items-center pr-3 mb-2>
        <p pl-4 w-12></p>
        <div flex="~ row" items-center>
          <StorageOverCap v-if="overflowNotMember && medias.length > 0" />
          <RActionButton
            ml-2
            mt-4
            :title="$t('action.add_story')"
            name="add_story"
            :serviceId="serviceId"
            @click="handleCreate"
            titleClass="md:text-theme-main">
            <div i-ri:heart-add-line text-lg mr-2 class="md:text-theme-main"></div>
          </RActionButton>
        </div>
      </div>
    </ClientOnly>
    <div w-full px-1 pt-2>
      <!-- Ensure Waterfall is robust or handled via client hydration where needed -->
      <ClientOnly>
        <template #default>
          <div v-if="medias && medias.length > 0" class="w-full">
            <Waterfall
              w-full
              :list="medias"
              :row-key="options.rowKey"
              :gutter="options.gutter"
              :has-around-gutter="options.hasAroundGutter"
              :width="options.width"
              :breakpoints="options.breakpoints"
              :img-selector="options.imgSelector"
              :background-color="options.backgroundColor"
              :animation-effect="options.animationEffect"
              :animation-duration="options.animationDuration"
              :animation-delay="options.animationDelay"
              :lazyload="options.lazyload"
              :load-props="options.loadProps"
              :cross-origin="options.crossOrigin"
              :align="options.align"
              @afterRender="afterRender">
              <template #default="{ item, url, index }">
                <MemorialPost
                  v-if="item.post"
                  :key="item.post.id"
                  :post="item.post"
                  :ref="(el: Element | ComponentPublicInstance | null) => setItemRef(el, item.post.id)"
                  :anim="playAnim && item.post.id == locatePostId"
                  :canEdit="canEdit"
                  @animationEnd="playAnim = false"
                  @onMediaClick="onPostMediaClicked"
                  @onDelete="onDelete">
                </MemorialPost>
              </template>
            </Waterfall>
          </div>
        </template>
        <template #fallback>
          <div v-if="medias && medias.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
            <div v-for="item in medias" :key="item.post.id" class="border rounded-lg p-2">
              <img v-if="item.src" :src="item.src" class="w-full h-auto object-cover rounded" />
              <p class="mt-2 text-sm">{{ item.name }}</p>
            </div>
          </div>
        </template>
      </ClientOnly>
    </div>
    <MFooter
      v-if="MfooterVisible"
      v-model="showFooter"
      :loading="footerStatus"
      :hasMore="hasMoreItems"
      @click="tryAgain" />
    <template #skeleton>
      <StoryListSkeleton />
    </template>
    <template #empty>
      <EmptyView :msg="$t('empty.no_story')" :theme="true">
        <ClientOnly>
          <div v-if="canEdit">
            <button btn-theme-outline bg-transparent rounded-full px-8 mt-20 @click="handleCreate">
              {{ $t('action.add_story') }}
            </button>
          </div>
        </ClientOnly>
      </EmptyView>
    </template>
    <ClientOnly>
      <div>
        <SetRealNameDialog ref="realNameModalRef" />
      </div>
    </ClientOnly>
  </BaseLayout>
</template>
<style lang="scss" scoped></style>
